import React, { useState } from "react";
import { Calendar } from "lucide-react";

const PRESETS = [
  { label: "Last 7 days", value: "last7" },
  { label: "Last 30 days", value: "last30" },
  { label: "This month", value: "thisMonth" },
  { label: "Last month", value: "lastMonth" },
  { label: "This year", value: "thisYear" },
  { label: "Last year", value: "lastYear" },
];
const UNITS = ["Day", "Week", "Month", "Quarter", "Year"];

export default function DateRangePicker({ onApply }: { onApply?: (range: any) => void }) {
  const [open, setOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState("last7");
  const [customNum, setCustomNum] = useState("");
  const [customUnit, setCustomUnit] = useState("Day");
  const [tab, setTab] = useState<"last" | "range">("last");

  // For Range tab
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);

  const handleApply = () => {
    let result;
    if (tab === "last") {
      if (customNum) {
        result = { type: "custom", number: Number(customNum), unit: customUnit };
      } else {
        result = { type: "preset", preset: selectedPreset };
      }
    } else if (tab === "range") {
      result = { type: "range", startDate, endDate };
    }
    onApply?.(result);
    setOpen(false);
  };

  const isAllTime = !startDate && !endDate;

  return (
    <div className="relative">
      <button
        className="border px-3 py-1.5 rounded-md flex items-center gap-2 bg-white"
        onClick={() => setOpen((v) => !v)}
        type="button"
      >
        <Calendar size={16} />
        Date
      </button>
      {open && (
        <div className="absolute left-0 z-50 mt-2 min-w-[420px] bg-white rounded-lg shadow-xl border">
          <div className="flex divide-x">
            {/* Left: tabs and presets */}
            <div className="w-44 p-3">
              <div className="flex flex-col gap-1">
                <button
                  className={`text-sm px-2 py-1 rounded font-medium ${tab === "last" ? "bg-blue-50 text-blue-600" : "text-gray-700 hover:bg-gray-100"}`}
                  onClick={() => setTab("last")}
                  type="button"
                >
                  Last
                </button>
                <button
                  className={`text-sm px-2 py-1 rounded font-medium ${tab === "range" ? "bg-blue-50 text-blue-600" : "text-gray-700 hover:bg-gray-100"}`}
                  onClick={() => setTab("range")}
                  type="button"
                >
                  Range
                </button>
                <hr className="my-2" />
                {PRESETS.map((p) => (
                  <button
                    key={p.value}
                    className={`w-full text-left px-2 py-1 rounded text-sm ${selectedPreset === p.value && tab === "last" ? "bg-blue-100 text-blue-700" : "text-gray-600 hover:bg-gray-100"}`}
                    onClick={() => {
                      setTab("last");
                      setSelectedPreset(p.value);
                      setCustomNum("");
                    }}
                    type="button"
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            {/* Right: "Last" or "Range" content */}
            <div className="flex-1 p-3 min-w-[220px]">
              {tab === "last" ? (
                <>
                  <div className="text-sm font-semibold text-gray-700 mb-2">Number</div>
                  <input
                    type="number"
                    className="border w-full rounded px-2 py-1 mb-4 text-sm"
                    placeholder="Enter number"
                    value={customNum}
                    min={1}
                    onChange={(e) => setCustomNum(e.target.value)}
                  />
                  <div className="text-sm font-semibold text-gray-700 mb-2">Unit</div>
                  <div className="flex flex-col gap-2">
                    {UNITS.map((u) => (
                      <label className="flex items-center gap-2 text-sm" key={u}>
                        <input
                          type="radio"
                          name="unit"
                          checked={customUnit === u}
                          onChange={() => setCustomUnit(u)}
                        />
                        {u}
                      </label>
                    ))}
                  </div>
                </>
              ) : (
                <div>
                  <div className="text-sm font-semibold text-gray-700 mb-2">
                    {isAllTime ? "All time" : ""}
                  </div>
                  <div className="mb-3">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-semibold text-gray-500">Start date</label>
                      <button
                        className="text-xs text-blue-600"
                        onClick={() => setStartDate(null)}
                        disabled={!startDate}
                        tabIndex={-1}
                        type="button"
                      >
                        Clear
                      </button>
                    </div>
                    <div className="relative">
                      <input
                        type="date"
                        className={`border w-full rounded px-2 py-1 text-sm placeholder:text-gray-300 ${!startDate ? "text-gray-300" : "text-gray-900"}`}
                        value={startDate || ""}
                        placeholder="Earliest"
                        onChange={(e) => setStartDate(e.target.value || null)}
                      />
                      {!startDate && (
                        <span className="pointer-events-none absolute left-2 top-1.5 text-xs text-gray-300">Earliest</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-semibold text-gray-500">End date</label>
                      <button
                        className="text-xs text-blue-600"
                        onClick={() => setEndDate(null)}
                        disabled={!endDate}
                        tabIndex={-1}
                        type="button"
                      >
                        Clear
                      </button>
                    </div>
                    <div className="relative">
                      <input
                        type="date"
                        className={`border w-full rounded px-2 py-1 text-sm placeholder:text-gray-300 ${!endDate ? "text-gray-300" : "text-gray-900"}`}
                        value={endDate || ""}
                        placeholder="Latest"
                        onChange={(e) => setEndDate(e.target.value || null)}
                      />
                      {!endDate && (
                        <span className="pointer-events-none absolute left-2 top-1.5 text-xs text-gray-300">Latest</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Actions */}
          <div className="flex justify-between items-center border-t px-4 py-2 bg-white rounded-b-lg">
            <button
              className="text-gray-500 hover:text-black text-xs font-medium"
              onClick={() => {
                setCustomNum("");
                setCustomUnit("Day");
                setTab("last");
                setSelectedPreset("last7");
                setStartDate(null);
                setEndDate(null);
                setOpen(false);
              }}
              type="button"
            >
              Clear
            </button>
            <div>
              <button
                className="text-gray-500 hover:text-black text-xs font-medium mr-2"
                onClick={() => setOpen(false)}
                type="button"
              >
                Cancel
              </button>
              <button
                className={`px-4 py-1.5 rounded font-medium text-white text-xs bg-orange-400 hover:bg-orange-500`}
                onClick={handleApply}
                type="button"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
